import React, { useCallback, useMemo } from "react";
import { fetchQuery, graphql, useLazyLoadQuery } from "react-relay";
import type {
	MenuListProps,
	MultiValueGenericProps,
	MultiValueRemoveProps,
	OnChangeValue,
	OptionsOrGroups,
	SingleValueProps,
	StylesConfig,
} from "react-select";
import AsyncCreatable, { type AsyncCreatableProps } from "react-select/async-creatable";

import { faTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faHashtag, faLock, faPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { throttle } from "lodash";

import { zindexModal } from "~/css/zindexes";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { withSuspense } from "~/lib/suspense";
import { isValidEmail, reduceEdgesToNodes } from "~/lib/utils";
import { environment } from "~/models";
import { CreateChannelModal, type UpdatedChannel } from "~/recordings/CreateEditChannelModal/CreateEditChannelModal";
import { eventEmitter as recordingsEventEmitter } from "~/recordings/events";
import { Avatar, LoadingSpinner, useModals } from "~/ui";
import fellowSelectProps from "~/ui/react-select";
import {
	components,
	type GroupBase,
	type OptionProps,
	type SelectorOption,
} from "~/ui/selectors/AsyncCreatableSelector";
import { IndicatorsContainer } from "~/ui/selectors/MultiSelectorUtils";
import { outlinedStyles } from "~/ui/selectors/SelectorUtils";

import type { Contact } from "../common/EmailListInput";

import type { ContactChannelSelectorOptionsQuery as OptionsQueryType } from "~/__generated__/ContactChannelSelectorOptionsQuery.graphql";
import type {
	ContactChannelSelectorQuery$data as QueryResponse,
	ContactChannelSelectorQuery as QueryType,
} from "~/__generated__/ContactChannelSelectorQuery.graphql";

import styles from "./css/ContactChannelSelector.module.css";

export type ContactChannelOption = SelectorOption & {
	contact?: Contact;
};

const Query = graphql`
	query ContactChannelSelectorQuery {
		viewer {
			account {
				allowInviting
				guestUsersAllowInvite
				allowExternalInviting
				isPersonalType
				emailDomainList
			}
		}
	}
`;

const OptionsQuery = graphql`
	query ContactChannelSelectorOptionsQuery(
		$search: String
		$skipChannels: Boolean!
		$skipContacts: Boolean!
		$skipUsers: Boolean!
		$userIdsToExclude: [ID!]!
	) {
		viewer {
			contacts(search: $search, first: 10, ranked: true, excludeInactive: true, excludeExternal: true)
				@skip(if: $skipContacts) {
				edges {
					node {
						...ContactSearch_contact @relay(mask: false)
					}
				}
			}

			channels(search: $search, excludePersonal: true, orderBy: NAME_ASC) @skip(if: $skipChannels) {
				id
				name
				isPrivate
			}

			users(first: 10, search: $search, isGuests: false, activeOnly: true, exclude: $userIdsToExclude)
				@skip(if: $skipUsers) {
				edges {
					node {
						id
						email
						firstName
						fullName
						image
						isActive
						isConfigured
						isGuest
						internalAttributes {
							oneOnOneWithViewer {
								id
								archived
							}
						}
					}
				}
			}
		}
	}
`;

const SELECTOR_STYLES: StylesConfig = {
	...outlinedStyles,
	multiValueRemove: (base, _state) => ({
		...base,
		cursor: "pointer",
		borderRadius: "14px",
		color: "var(--secondaryTextColor)",
		background: "transparent",
		marginLeft: "0.25rem",
		padding: "0.25rem",
		width: "2em",
		height: "2em",

		":hover": {
			background: "var(--mutedGrey)",
			color: "var(--placeholderTextColor)",
		},
	}),
	multiValue: (base, _state) => ({
		...base,
		cursor: "default",
		borderRadius: "14px",
		color: "var(--vividGrey)",
		background: "var(--thirdLayerColor)",
		margin: "0.25rem",
		alignItems: "center",
		height: "2em",
	}),
	valueContainer: (base, _state) => ({ ...base, padding: "0" }),
	control: (base, state) => ({
		...outlinedStyles.control?.(base, state),
		"> :last-child": {},
		":hover > :last-child": { backgroundColor: outlinedStyles.control?.(base, state).backgroundColor },
		minHeight: "2.375rem",
	}),
	menuPortal: provided => ({ ...provided, zIndex: zindexModal + 1 }),
	menu: (base, state) => ({
		...outlinedStyles.menu?.(base, state),
		minWidth: "initial",
	}),
};

function MultiValueRemove({
	children: _children,
	data,
	innerProps,
	...rest
}: MultiValueRemoveProps<ContactChannelOption>) {
	return (
		<components.MultiValueRemove {...rest} data={data} innerProps={innerProps}>
			<FontAwesomeIcon fixedWidth icon={faTimes} />
		</components.MultiValueRemove>
	);
}

function MultiValueLabel({ children, data, innerProps, ...rest }: MultiValueGenericProps<ContactChannelOption>) {
	const contact = useMemo(
		() => ({
			...data.contact,
			fullName: data.contact?.fullName || data.contact?.email || data.label,
		}),
		[data],
	);

	return (
		<components.MultiValueLabel {...rest} data={data} innerProps={innerProps}>
			<div className={styles.multiValueLabel}>
				{data.contact ? (
					<Avatar user={contact} className={styles.avatar} size="large" />
				) : (
					<>{data.icon && <FontAwesomeIcon fixedWidth icon={data.icon} className={styles.icon} />}</>
				)}
				<span className={styles.label}>{children}</span>
			</div>
		</components.MultiValueLabel>
	);
}

function SingleValue({ children, data, innerProps, ...rest }: SingleValueProps<ContactChannelOption>) {
	const contact = useMemo(
		() => ({
			...data.contact,
			fullName: data.contact?.fullName || data.contact?.email || data.label,
		}),
		[data],
	);

	return (
		<components.SingleValue {...rest} data={data} innerProps={innerProps}>
			<div className={styles.multiValueLabel}>
				{data.contact ? (
					<Avatar user={contact} className={styles.avatar} size="large" />
				) : (
					<>{data.icon && <FontAwesomeIcon fixedWidth icon={data.icon} className={styles.icon} />}</>
				)}
				<span className={styles.label}>{children}</span>
			</div>
		</components.SingleValue>
	);
}

function Option({ children, data, innerProps, ...rest }: OptionProps<ContactChannelOption>) {
	const { isGuest, isExternal, hasConfiguredUser } = data.contact ?? {};
	const newLabel = isGuest ? "+ New guest" : isExternal ? "+ New external" : "+ New teammate";
	const contact = useMemo(
		() => ({
			...data.contact,
			fullName: data.contact?.fullName || data.contact?.email || data.label,
		}),
		[data],
	);
	return (
		<components.Option {...rest} data={data} innerProps={innerProps} className={styles.option}>
			{data.contact ? (
				<Avatar user={contact} className={styles.avatar} />
			) : (
				<>{data.icon && <FontAwesomeIcon fixedWidth icon={data.icon} className={styles.icon} />}</>
			)}
			<div className={styles.textContainer}>
				<p className={styles.label}>{children}</p>
				{data.contact && <small className={styles.smallText}>{data.contact.email}</small>}
			</div>
			{data.contact && !hasConfiguredUser ? <p className={styles.plusBtn}>{newLabel}</p> : <></>}
		</components.Option>
	);
}

type SelectorProps = AsyncCreatableProps<ContactChannelOption, boolean, GroupBase<ContactChannelOption>> & {
	account: QueryResponse["viewer"]["account"];
	skipChannels?: boolean;
	skipContacts?: boolean;
	skipUsers?: boolean;
	afterNewChannelCreated: (channel: UpdatedChannel) => void;
	userIdsToExclude: readonly string[];
};

type ContactChannelMenuListProps = {
	onNewChannelOptionClick?: () => void;
} & MenuListProps<ContactChannelOption, boolean, GroupBase<ContactChannelOption>>;

function ContactChannelMenuList(props: ContactChannelMenuListProps) {
	return (
		<components.MenuList {...props}>
			{props.children}

			{props.onNewChannelOptionClick && <div className={styles.divider} />}

			{props.onNewChannelOptionClick && (
				<button className={styles.action} onClick={props.onNewChannelOptionClick}>
					<FontAwesomeIcon fixedWidth icon={faPlus} className={styles.icon} />
					<div className={styles.textContainer}>
						<p className={styles.label}>Create new channel</p>
					</div>
				</button>
			)}
		</components.MenuList>
	);
}

function Selector({
	account,
	userIdsToExclude,
	skipChannels = false,
	skipContacts = false,
	skipUsers = true,
	components: customComponents,
	className,
	afterNewChannelCreated,
	...rest
}: SelectorProps) {
	const selectorReference = React.useRef(null);
	const modals = useModals();

	const [searchValue, setSearchValue] = React.useState("");
	const updateSearchValue = useCallback((value: string) => {
		setSearchValue(value);
	}, []);

	const refetchChannels = React.useCallback(() => {
		recordingsEventEmitter.emit("Channels.refetch");
	}, []);

	const lowerCaseDomainList = account.emailDomainList.map(domain => domain.toLowerCase());

	const makeDeferNewChannelInfoWhenAvailable = useCallback(
		(close: (result?: UpdatedChannel) => void) => {
			return (result?: UpdatedChannel) => {
				if (result) {
					afterNewChannelCreated(result);
				} else {
					console.error("Unexpected response from createChannel mutation", result);
				}

				// This is a workaround which covers the events of channel creation and modal dismissal.
				//
				// For some unknown reason, the Selector component is stuck in the state where the component
				// rejects the click input after the modal is dismissed on the channel creation or abortion via
				// the close and cancel buttons. However, once the component is de-focused, it then will accept
				// the click input.
				//
				// The workaround is to reset the state of the selector by de-focusing from and refocusing on the
				// component when the modal is dismissed on both.
				// @ts-ignore
				selectorReference.current?.blur();
				// @ts-ignore
				selectorReference.current?.focus();

				close(result);
			};
		},
		[afterNewChannelCreated],
	);

	const openCreateChannelModal = useCallback(() => {
		dispatchAnalyticsTrack("Open add channel modal", { location: "ContactChannelSelector" });
		modals.open<UpdatedChannel>(({ close }) => {
			const deferNewChannelInfoWhenAvailable = makeDeferNewChannelInfoWhenAvailable(close);
			return (
				<CreateChannelModal
					newChannel={{ name: searchValue }}
					refetchChannels={refetchChannels}
					onClose={deferNewChannelInfoWhenAvailable}
				/>
			);
		});
	}, [modals, refetchChannels, makeDeferNewChannelInfoWhenAvailable, searchValue]);

	const loadOptions = React.useCallback(
		(search: string, callback: (options: ContactChannelOption[]) => void) => {
			fetchQuery<OptionsQueryType>(environment, OptionsQuery, {
				search: search,
				skipChannels,
				skipContacts,
				skipUsers,
				userIdsToExclude,
			})
				.toPromise()
				.then(result => {
					if (!result) {
						return;
					}

					const { contacts: contactEdges, channels, users: userEdges } = result.viewer;

					const contacts = contactEdges ? reduceEdgesToNodes(contactEdges.edges) : [];
					const contactOptions = contacts
						.map(contact => ({
							label: contact.fullName,
							value: contact.email,
							contact: {
								...contact,
								hasConfiguredUser: contact.user?.isConfigured,
								isGuest: contact.user?.isGuest,
								isExternal:
									!account.isPersonalType && !lowerCaseDomainList.includes(contact.email.split("@")[1].toLowerCase()),
							},
						}))
						.filter(
							option =>
								option.contact.hasConfiguredUser ||
								(lowerCaseDomainList.includes(option.contact.email.split("@")[1].toLowerCase())
									? account.allowInviting
									: account.guestUsersAllowInvite || account.allowExternalInviting),
						);

					// For backwards compability and avoid breaking changes, we are using same schema between contacts and users
					// So we map users into contact options
					const users = userEdges ? reduceEdgesToNodes(userEdges.edges) : [];
					const userOptions = users
						.map(user => ({
							label: user.fullName,
							value: user.email,
							contact: {
								id: user.id,
								email: user.email,
								isGuest: user.isGuest,
								isExternal:
									!account.isPersonalType && !lowerCaseDomainList.includes(user.email.split("@")[1].toLowerCase()),
								hasConfiguredUser: user.isConfigured,
								image: user.image,
								fullName: user.fullName,
								user: user,
							},
						}))
						.filter(
							option =>
								option.contact.hasConfiguredUser ||
								(lowerCaseDomainList.includes(option.contact.email.split("@")[1].toLowerCase())
									? account.allowInviting
									: account.guestUsersAllowInvite || account.allowExternalInviting),
						);

					const channelOptions =
						channels?.map(channel => ({
							label: channel.name,
							value: channel.id,
							icon: channel.isPrivate ? faLock : faHashtag,
						})) ?? [];

					// always use users if available
					const userOrContactOptions = userOptions.length > 0 ? userOptions : contactOptions;

					callback([...channelOptions, ...userOrContactOptions].sort((a, b) => a.label.localeCompare(b.label)));
				});
		},
		[lowerCaseDomainList, account, skipChannels, skipContacts, userIdsToExclude, skipUsers],
	);

	const throttledLoadOptions = React.useMemo(() => throttle(loadOptions, 600), []); // eslint-disable-line react-hooks/exhaustive-deps

	React.useEffect(() => {
		return () => throttledLoadOptions.cancel();
	}, [throttledLoadOptions]);

	const components = {
		IndicatorsContainer,
		Option,
		MultiValueLabel,
		MultiValueRemove,
		SingleValue,
		MenuList: (props: ContactChannelMenuListProps) => {
			return (
				<ContactChannelMenuList
					{...props}
					onNewChannelOptionClick={isValidEmail(searchValue) ? undefined : openCreateChannelModal}
				/>
			);
		},
		...customComponents,
	};

	const isValidNewOption = React.useCallback(
		(
			inputValue: string,
			value: OnChangeValue<ContactChannelOption, boolean>,
			options: OptionsOrGroups<ContactChannelOption, GroupBase<ContactChannelOption>>,
		) => {
			if (skipContacts) {
				return false;
			}

			const trimmedInput = inputValue.trim().toLowerCase();
			const isInternalEmail = trimmedInput.includes("@") && lowerCaseDomainList.includes(trimmedInput.split("@")[1]);

			if (isInternalEmail) {
				if (!account.allowInviting) {
					// no internal invitations allowed
					return false;
				}
			} else {
				// no external invitations allowed, regardless of account settings (guestUsersAllowInvite or allowExternalInviting)
				return false;
			}

			const emailFound = options.find(option =>
				"contact" in option ? option.contact?.email.toLowerCase() === trimmedInput : false,
			);

			return isValidEmail(trimmedInput) && !emailFound;
		},
		[lowerCaseDomainList, account, skipContacts],
	);

	const getNewOptionData = React.useCallback(
		(value: string) => {
			const trimmedValue = value.trim().toLowerCase();
			const isInternalEmail = trimmedValue.includes("@") && lowerCaseDomainList.includes(trimmedValue.split("@")[1]);
			const isExternalEmail = !isInternalEmail && !account.isPersonalType;

			const isGuest = isExternalEmail && !account.allowExternalInviting;
			const isExternal = isExternalEmail && !!account.allowExternalInviting;

			const contact = { id: "0", email: value, isGuest, isExternal };
			return { label: value, value, contact };
		},
		[account, lowerCaseDomainList],
	);

	return (
		<AsyncCreatable<ContactChannelOption, boolean>
			{...rest}
			ref={selectorReference}
			loadOptions={throttledLoadOptions}
			components={components}
			styles={SELECTOR_STYLES as StylesConfig<ContactChannelOption>}
			className={cx(styles.selector, className)}
			classNamePrefix={fellowSelectProps.classNamePrefix}
			menuPortalTarget={document.body}
			isValidNewOption={isValidNewOption}
			getNewOptionData={getNewOptionData}
			onInputChange={updateSearchValue}
		/>
	);
}

function SelectorRenderer(props: Omit<SelectorProps, "account" | "skipUsers">) {
	const {
		viewer: { account },
	} = useLazyLoadQuery<QueryType>(Query, {}, { fetchPolicy: "store-and-network" });

	const { skipContacts } = props;

	// Contact query has significant performance implications for large clients (could take 30s to load or more)
	// As a temporary solution, we are using this FF for big accounts, so we fetch users instead of contacts
	// Fetching users instead of contacts means we won't list contacts that require invitation anymore
	// so if the FF is on for this account, this account will only be able to share with existing users
	const shouldFetchUsers = useFeatureIsOn("fetch-users-in-contact-channel-selector");

	// if the caller has explicitly set skipContacts, we don't want to override it
	// otherwise, we want to fetch users instead of contacts if the feature flag is on
	const skipProps = skipContacts
		? { skipContacts: true, skipUsers: true }
		: {
				skipContacts: shouldFetchUsers,
				skipUsers: !shouldFetchUsers,
			};

	return <Selector {...props} account={account} {...skipProps} />;
}

const SelectorRendererWithSuspense = withSuspense(<LoadingSpinner />, SelectorRenderer);

export default SelectorRendererWithSuspense;
